import { Copy } from "lucide-react";
import { ContactAvatar } from "@/features/contacts/ContactAvatar";
import { extractEmailAddress } from "@/lib/compose/composeDraft";
import { extractSenderLabel } from "@/lib/formatMailDate";
import { showMailToast } from "@/lib/ui/mailToast";

type Props = {
  from: string;
  to?: string;
  cc?: string;
  date: string;
  accountEmail?: string;
};

function formatMobileReaderDate(raw: string): string {
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;

  const now = new Date();
  const time = parsed.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const sameDay =
    parsed.getDate() === now.getDate() &&
    parsed.getMonth() === now.getMonth() &&
    parsed.getFullYear() === now.getFullYear();
  if (sameDay) return time;

  const day = parsed.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    ...(parsed.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${day} · ${time}`;
}

function splitAddressList(raw: string): string[] {
  const parts: string[] = [];
  let buf = "";
  let depth = 0;
  let quote = "";
  for (const ch of raw) {
    if (quote) {
      buf += ch;
      if (ch === quote) quote = "";
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "<") depth += 1;
    if (ch === ">") depth = Math.max(0, depth - 1);
    if (ch === "," && depth === 0) {
      if (buf.trim()) parts.push(buf.trim());
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) parts.push(buf.trim());
  return parts;
}

function recipientShort(raw: string, accountEmail: string): string {
  const email = extractEmailAddress(raw).trim().toLowerCase();
  if (accountEmail && email === accountEmail.trim().toLowerCase()) return "moi";
  const label = extractSenderLabel(raw).trim();
  if (label && label.toLowerCase() !== email) return label;
  const local = email.split("@")[0];
  return local || label || email || raw;
}

function formatRecipientLine(raw: string, accountEmail: string): string {
  const parts = splitAddressList(raw);
  if (parts.length === 0) return "";
  const labels = parts.map((part) => recipientShort(part, accountEmail));
  if (labels.length <= 2) return labels.join(", ");
  return `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
}

async function copyEmail(email: string) {
  try {
    await navigator.clipboard.writeText(email);
    showMailToast("Adresse copiée", "success", 2200);
  } catch {
    showMailToast("Copie impossible", "error", 2800);
  }
}

/** Bloc expéditeur / destinataires du lecteur mobile — une info par ligne. */
export function MobileReaderMeta({ from, to = "", cc = "", date, accountEmail = "" }: Props) {
  const senderName = extractSenderLabel(from) || "Expéditeur inconnu";
  const senderEmail = extractEmailAddress(from).trim().toLowerCase();
  const showEmail = Boolean(senderEmail && senderName.trim().toLowerCase() !== senderEmail);
  const dateLabel = formatMobileReaderDate(date);
  const toLine = formatRecipientLine(to, accountEmail);
  const ccLine = formatRecipientLine(cc, accountEmail);

  return (
    <div className="mobile-reader-meta">
      <ContactAvatar label={senderName} fromRaw={from} size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="min-w-0 truncate text-[15px] font-semibold leading-tight text-[var(--mail-text)]">
            {senderName}
          </p>
          {dateLabel ? (
            <span className="ml-auto shrink-0 text-[11px] tabular-nums text-[var(--mail-muted)]">
              {dateLabel}
            </span>
          ) : null}
        </div>
        {showEmail ? (
          <div className="mt-0.5 flex min-w-0 items-center gap-1">
            <p className="min-w-0 truncate text-[12px] text-[var(--mail-muted)]">{senderEmail}</p>
            <button
              type="button"
              onClick={() => void copyEmail(senderEmail)}
              aria-label="Copier l’adresse"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--mail-muted)] active:bg-[var(--mail-surface-hover)]"
            >
              <Copy className="h-3 w-3" aria-hidden />
            </button>
          </div>
        ) : null}
        {toLine || ccLine ? (
          <dl className="mobile-reader-meta__recipients">
            {toLine ? (
              <div className="mobile-reader-meta__row">
                <dt>À</dt>
                <dd title={to}>{toLine}</dd>
              </div>
            ) : null}
            {ccLine ? (
              <div className="mobile-reader-meta__row">
                <dt>Cc</dt>
                <dd title={cc}>{ccLine}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </div>
    </div>
  );
}
